import { Close, ContentCopy, Visibility } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  Modal,
  CircularProgress,
  IconButton,
  Grid,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { getGoogleDriveId } from "../../utils/generalFunction";

interface PropertiesSearchProps {
  data: any[];
  materialType: string[];
  productType: string[];
  onSearch: (filteredData: any[]) => void;
  accessToken?: string;
  initialProductCode?: string; // Thêm prop cho Code
}

const PropertiesSearch: React.FC<PropertiesSearchProps> = (props) => {
  const {
    data,
    materialType,
    productType,
    onSearch,
    accessToken,
    initialProductCode,
  } = props;
  const [materialTypeSelected, setMaterialTypeSelected] = useState<string>("");
  const [productTypeSelected, setProductTypeSelected] = useState<string[]>([]);
  const [priceFrom, setPriceFrom] = useState<string>("");
  const [priceTo, setPriceTo] = useState<string>("");
  const [productCode, setProductCode] = useState<string>("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isCopyError, setIsCopyError] = useState(false);

  const handleChangeMaterialType = (event: SelectChangeEvent<string>) => {
    setMaterialTypeSelected(event.target.value as string);
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Lọc theo Wood type
      const matchMaterial =
        !materialTypeSelected || item["Wood type"] === materialTypeSelected;

      // Lọc theo Category
      const matchProductType =
        productTypeSelected.length === 0 ||
        productTypeSelected.some((type) => item["Category"]?.includes(type));

      // Lọc theo Price From/To
      const priceFromNum = priceFrom
        ? parseFloat(priceFrom.replace(/[^0-9.]/g, ""))
        : null;
      const priceToNum = priceTo
        ? parseFloat(priceTo.replace(/[^0-9.]/g, ""))
        : null;
      const itemPriceFrom = parseFloat(
        item["Price From"]?.replace(/[^0-9.]/g, "")
      );
      const itemPriceTo = parseFloat(item["Price To"]?.replace(/[^0-9.]/g, ""));

      const matchPriceFrom =
        !priceFromNum || (itemPriceFrom && itemPriceFrom >= priceFromNum);
      const matchPriceTo =
        !priceToNum || (itemPriceTo && itemPriceTo <= priceToNum);

      // Lọc theo Product Code
      const matchCode =
        !productCode ||
        item["Code"]?.toLowerCase().includes(productCode.toLowerCase());

      return (
        matchMaterial &&
        matchProductType &&
        matchPriceFrom &&
        matchPriceTo &&
        matchCode
      );
    });
  }, [
    data,
    materialTypeSelected,
    productTypeSelected,
    priceFrom,
    priceTo,
    productCode,
  ]);

  const handleSearch = () => {
    onSearch(filteredData);
  };
  // Copy thông tin sản phẩm vào clipboard
  const handleCopy = (item: any) => {
    var contentCopy = "";
    contentCopy += "Em gửi mình thông tin tác phẩm ạ. \n";
    contentCopy += " - Mã Tác Phẩm: " + item["Code"] + "\n";
    contentCopy += " - Chất liệu: Nu " + item["Wood type"] + "\n";
    if (item["Diameter"] && item["Diameter"] !== "") {
      contentCopy += " - Đường kính: " + item["Diameter"] + " cm \n";
      contentCopy += " - Cao: " + item["Height"] + " cm \n";
    } else {
      contentCopy +=
        " - Kích thước: " +
        item["Width"] +
        " x " +
        item["Height"] +
        " x " +
        item["Depth"] +
        " cm \n";
    }
    contentCopy +=
      " - Giá: " + item["Price To"].slice(0, -1).trim() + " triệu \n";
    navigator.clipboard
      .writeText(contentCopy)
      .then(() => {
        setIsCopyError(false);
        setOpenSnackbar(true);
      })
      .catch((err) => {
        setIsCopyError(true);
        console.error("Lỗi khi copy:", err);
      });
    setOpenModal(false);
  };

  // Mở modal và tải hình ảnh
  const handleOpenModal = async (item: any) => {
    setSelectedProduct(item);
    setOpenModal(true);
    setImageUrls([]);
    setImageLoading(true);

    if (accessToken && item["Code"]) {
      try {
        // Tìm folder có tên khớp với Code
        const folderResponse = await axios.get(
          `https://www.googleapis.com/drive/v3/files?q=name='${item["Code"]}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const folder = folderResponse.data.files[0];
        if (!folder) {
          setImageLoading(false);
          return;
        }

        // Lấy danh sách file hình ảnh trong folder
        const filesResponse = await axios.get(
          `https://www.googleapis.com/drive/v3/files?q='${folder.id}'+in+parents+and+mimeType+contains+'image/' and trashed=false&fields=files(id)`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const imageIds = filesResponse.data.files.map((file: any) => file.id);
        const urls = imageIds.map(
          (id: string) => `https://drive.google.com/uc?export=view&id=${id}`
        );
        setImageUrls(urls);
      } catch (err) {
        console.error("Lỗi khi tải hình ảnh:", err);
      }
    }
    setImageLoading(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedProduct(null);
    setImageUrls([]);
  };

  useEffect(() => {
    if (initialProductCode) {
      setProductCode(initialProductCode);
      handleSearch();
    }
  }, [initialProductCode]);

  return (
    <div>
      <Box className="flex">
        <FormControl sx={{ mt: 2, mr: 1, width: "100%" }}>
          <InputLabel
            id="demo-simple-select-autowidth-label "
            sx={{ fontSize: "0.9rem", top: "-6px" }}
          >
            Loại gỗ
          </InputLabel>
          <Select
            labelId="materialType"
            id="demo-simple-select-autowidth"
            value={materialTypeSelected}
            onChange={handleChangeMaterialType}
            label="Loại gỗ"
            sx={{
              height: "40px",
              fontSize: "0.9rem",
              "& .MuiSelect-select": {
                padding: "8px 12px",
              },
            }}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {materialType.map((type, index) => (
              <MenuItem key={index} value={type} sx={{ fontSize: "0.9rem" }}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mt: 2, width: "100%" }}>
          <Autocomplete
            multiple
            freeSolo
            value={productTypeSelected}
            onChange={(event, newValue) => {
              setProductTypeSelected(newValue);
            }}
            options={productType}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  size="small"
                  label={option}
                  sx={{ height: "24px", fontSize: "0.8rem" }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Loại sản phẩm"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    fontSize: "0.9rem",
                    padding: "0 8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                  },
                }}
              />
            )}
          />
        </FormControl>
      </Box>

      <Box className="flex">
        <FormControl sx={{ mt: 2, mr: 1, width: "100%" }}>
          <TextField
            id="price-from"
            label="Giá từ"
            placeholder="Giá từ"
            variant="outlined"
            value={priceFrom}
            onChange={(e) => setPriceFrom(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                fontSize: "0.9rem",
                padding: "0 8px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.9rem",
                top: "-6px",
              },
            }}
          />
        </FormControl>

        <FormControl sx={{ mt: 2, width: "100%" }}>
          <TextField
            id="price-to"
            label="Giá đến"
            placeholder="Giá đến"
            variant="outlined"
            value={priceTo}
            onChange={(e) => setPriceTo(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                fontSize: "0.9rem",
                padding: "0 8px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.9rem",
                top: "-6px",
              },
            }}
          />
        </FormControl>
      </Box>

      <FormControl sx={{ mt: 2, width: "100%" }}>
        <TextField
          id="product-code"
          label="Mã sản phẩm"
          placeholder="Mã sản phẩm"
          variant="outlined"
          value={productCode}
          onChange={(e) => setProductCode(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              height: "40px",
              fontSize: "0.9rem",
              padding: "0 8px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.9rem",
              top: "-6px",
            },
          }}
        />
      </FormControl>

      {filteredData.length > 0 ? (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table aria-label="kết quả lọc">
            <TableHead>
              <TableRow>
                <TableCell align="center">Actions</TableCell>
                <TableCell className="w-24">Code</TableCell>
                <TableCell>Giá Từ</TableCell>
                <TableCell>Giá Đến</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <IconButton onClick={() => handleCopy(item)} size="small">
                      <ContentCopy />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenModal(item)}
                      size="small"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                  <TableCell>{item["Code"]}</TableCell>
                  <TableCell>{item["Price From"]}</TableCell>
                  <TableCell>{item["Price To"]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ m: 2, color: "text.secondary" }}>
          Không tìm thấy sản phẩm phù hợp.
        </Typography>
      )}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        {selectedProduct ? (
          <>
            {/* Title luôn fix trên đầu */}
            <DialogTitle>
              Thông tin sản phẩm: {selectedProduct["Code"]}
            </DialogTitle>

            {/* Nội dung, có scroll nếu dài */}
            <DialogContent dividers sx={{ maxHeight: "70vh" }}>
              <Grid container spacing={2}>
                {Object.entries(selectedProduct).map(([key, value]) => (
                  <Grid key={key} size={{ xs: 12, sm: 6 }}>
                    <Typography fontWeight="bold">{key}:</Typography>
                    <Typography>{String(value)}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Hình ảnh:
              </Typography>

              {imageLoading ? (
                <CircularProgress />
              ) : imageUrls.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {imageUrls.map((url, index) => (
                    <img
                      key={index}
                      src={`https://images.weserv.nl/?url=https://drive.google.com/uc?id=${getGoogleDriveId(
                        url ?? ""
                      )}`}
                      alt={`Hình ${index + 1}`}
                      style={{
                        width: "30%",
                        objectFit: "contain",
                        borderRadius: "4px",
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography>Không tìm thấy hình ảnh.</Typography>
              )}
            </DialogContent>

            {/* Action luôn fix cuối */}
            <DialogActions className="!justify-center">
              <Button
                variant="contained"
                onClick={() => handleCopy(selectedProduct)}
                color="success"
                startIcon={<ContentCopy />}
              >
                Copy
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCloseModal}
                endIcon={<Close />}
              >
                Đóng
              </Button>
            </DialogActions>
          </>
        ) : (
          <Typography sx={{ p: 3 }}>Không có dữ liệu.</Typography>
        )}
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={isCopyError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {isCopyError
            ? "Lỗi khi copy thông tin sản phẩm!"
            : "Đã copy thông tin sản phẩm!"}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PropertiesSearch;
